import { useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Box } from '@/components/Box/Box';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { Paginator } from '@/components/Paginator/Paginator';
import { PageFooter } from '@/components/Page/PageFooter/PageFooter';
import { useGetSearchParams } from '@/hooks/useGetSearchParams';
import { useReplaceSearchParams } from '@/hooks/useReplaceSearchParams';
import { getListData } from '@/helpers/helpers';
import { InvoiceCheckType } from '@/types/invoiceVaultTypes';
import { INVOICE_VAULT_QUERY_KEYS, loadInvoiceChecks } from '../../api/api';
import { CheckInvoiceWizard } from './ui/CheckInvoiceWizard';
import { InvoiceChecksList } from './ui/InvoiceChecksList';
import { UnmaskDetailsModal } from './ui/UnmaskDetailsModal';
import { InvoiceCheckVerificationModal } from './ui/InvoiceCheckVerificationModal';
import { SearchInput } from '@/bundle/shared/components/SearchInput/SearchInput';
import { useSearch } from '@/bundle/shared/hooks/useSearch';
import { DetailsWidget } from '@/components/DetailsWidget/DetailsWidget';
import { PageColumn } from '@/components/Page/PageColumn/PageColumn';
import { PageContent } from '@/components/Page/PageContent/PageContent';

type ParamsType = { q?: string; archived?: string; page?: number; limit?: number };
type ModalType = { kind: 'unmask'; check: InvoiceCheckType } | { kind: 'verification'; check: InvoiceCheckType } | null;

export const CheckInvoiceTab = () => {
  const queryClient = useQueryClient();
  const searchParams = useGetSearchParams<ParamsType>();
  const { replaceSearchParams } = useReplaceSearchParams<ParamsType>();
  const [modal, setModal] = useState<ModalType>(null);
  const { search, setSearch, clearSearch, submitSearch } = useSearch('q');

  const { data: listResponse, isPending } = useQuery({
    queryKey: [INVOICE_VAULT_QUERY_KEYS.checks, searchParams],
    queryFn: () => loadInvoiceChecks(searchParams),
    placeholderData: keepPreviousData,
  });
  const { list, totalCount, hasPagination } = getListData(listResponse?.body, searchParams);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: [INVOICE_VAULT_QUERY_KEYS.checks] });

  const checkInvoiceSent = () => {
    invalidate();
  };

  return (
    <Box display='flex' flexDirection='column' gap='24px' marginTop='24px'>
      <PageContent>
        <PageColumn>
          <DetailsWidget header='Check an invoice before you pay' headerOffset={0}>
            <CheckInvoiceWizard onSent={checkInvoiceSent} />
          </DetailsWidget>
        </PageColumn>
        <PageColumn> </PageColumn>
      </PageContent>

      <Box display='flex' justifyContent='space-between' alignItems='center' gap='16px' marginTop={'16px'}>
        <SearchInput
          value={search}
          width='420px'
          name='invoice-search'
          placeholder='Search by vendor or invoice number'
          onChange={(value) => setSearch(value)}
          onClear={clearSearch}
          onSubmit={submitSearch}
          hasErrorComponent={false}
        />

        <ButtonNew
          variant='secondary'
          size='medium'
          onClick={() => replaceSearchParams({ archived: searchParams.archived === '1' ? undefined : '1', page: null })}
        >
          {searchParams.archived === '1' ? 'Show active' : 'Show archived'}
        </ButtonNew>
      </Box>

      <InvoiceChecksList
        list={list}
        isLoading={isPending}
        onChanged={invalidate}
        onUnmask={(check) => setModal({ kind: 'unmask', check })}
        onVerification={(check) => setModal({ kind: 'verification', check })}
      />

      {hasPagination && (
        <PageFooter>
          <Paginator page={searchParams.page} limit={searchParams.limit} total={totalCount} />
        </PageFooter>
      )}

      {modal?.kind === 'unmask' && <UnmaskDetailsModal check={modal.check} onClose={() => setModal(null)} />}

      {modal?.kind === 'verification' && (
        <InvoiceCheckVerificationModal id={modal.check?.id} onClose={() => setModal(null)} />
      )}
    </Box>
  );
};
